import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import type { DepartmentPeriodData } from '../../services/api';

interface DepartmentTableProps {
  departments: DepartmentPeriodData[];
}

const DEPT_COLORS: Record<string, string> = {
  'Membership Sales': '#8b5cf6',
  'Membership L4': '#a78bfa',
  'Membership L5': '#c4b5fd',
  'SP 30': '#7c3aed',
  'Club Sales (Callaway)': '#3b82f6',
  'School Sales': '#f59e0b',
  'Product Sales': '#22c55e',
  'Trade-In Sales': '#06b6d4',
  'Front-End Sales': '#ec4899',
};

const formatCurrency = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
};

export const DepartmentTable: React.FC<DepartmentTableProps> = ({ departments }) => {
  const totalNet = departments.reduce((sum, d) => sum + d.netRevenue, 0);
  const totalComparisonNet = departments.reduce((sum, d) => sum + d.comparisonNet, 0);
  const totalDeltaPct = totalComparisonNet !== 0
    ? Math.round(((totalNet - totalComparisonNet) / totalComparisonNet) * 1000) / 10
    : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-base font-semibold text-gray-900">Department Performance</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left px-5 py-2.5 text-gray-500 font-medium">Department</th>
              <th className="text-right px-3 py-2.5 text-gray-500 font-medium">Txns</th>
              <th className="text-right px-3 py-2.5 text-gray-500 font-medium">Net Revenue</th>
              <th className="text-right px-3 py-2.5 text-gray-500 font-medium">vs Last Year</th>
              <th className="text-right px-5 py-2.5 text-gray-500 font-medium">Share</th>
            </tr>
          </thead>
          <tbody>
            {departments.map(dept => {
              const share = totalNet > 0 ? (dept.netRevenue / totalNet) * 100 : 0;
              const isUp = dept.deltaPct !== null && dept.deltaPct > 0;
              const isDown = dept.deltaPct !== null && dept.deltaPct < 0;
              return (
                <tr key={dept.departmentId} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-5 py-3 flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: DEPT_COLORS[dept.name] || '#6b7280' }}
                    />
                    <span className="font-medium text-gray-900">{dept.name}</span>
                  </td>
                  <td className="text-right px-3 py-3 text-gray-600">{dept.txCount.toLocaleString()}</td>
                  <td className="text-right px-3 py-3 font-semibold text-gray-900">{formatCurrency(dept.netRevenue)}</td>
                  <td className="text-right px-3 py-3">
                    {dept.deltaPct !== null ? (
                      <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${
                        isUp ? 'text-emerald-600' : isDown ? 'text-red-600' : 'text-gray-400'
                      }`}>
                        {isUp ? <ArrowUpRight size={12} /> : isDown ? <ArrowDownRight size={12} /> : null}
                        {Math.abs(dept.deltaPct).toFixed(1)}%
                      </span>
                    ) : <span className="text-gray-300">--</span>}
                  </td>
                  <td className="text-right px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${share}%`, backgroundColor: DEPT_COLORS[dept.name] || '#6b7280' }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-10 text-right">{share.toFixed(0)}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 font-semibold">
              <td className="px-5 py-3 text-gray-900">Total</td>
              <td className="text-right px-3 py-3 text-gray-600">
                {departments.reduce((sum, d) => sum + d.txCount, 0).toLocaleString()}
              </td>
              <td className="text-right px-3 py-3 text-gray-900">{formatCurrency(totalNet)}</td>
              <td className="text-right px-3 py-3">
                {totalDeltaPct !== null && (
                  <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${
                    totalDeltaPct > 0 ? 'text-emerald-600' : totalDeltaPct < 0 ? 'text-red-600' : 'text-gray-400'
                  }`}>
                    {totalDeltaPct > 0 ? <ArrowUpRight size={12} /> : totalDeltaPct < 0 ? <ArrowDownRight size={12} /> : null}
                    {Math.abs(totalDeltaPct).toFixed(1)}%
                  </span>
                )}
              </td>
              <td className="text-right px-5 py-3 text-gray-500 text-xs">100%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
